/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    CircularProgress,
    Tooltip,
    IconButton,
} from "@mui/material";
import {
    Search,
    Refresh,
    SyncAlt,
    OpenInNew,
    MailOutline,
    PhoneOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchCustomers,
    syncCustomers,
} from "../../redux/actions/collectAction";
import {
    selectActiveBusinessId,
    selectCustomers,
    selectIsLoadingCustomers,
    selectCustomersError,
    selectCustomersCursor,
    selectIsSyncing,
} from "../../redux/selectors/collectSelector";
import { ICollectCustomer } from "../../utility/types/collectTypes";
import { colors } from "../../utility/types/constant";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectInfoBanner,
    FlexBetween,
} from "./styles";

function formatMoney(value: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
        }).format(value || 0);
    } catch {
        return `$${(value || 0).toFixed(2)}`;
    }
}

const CollectCustomers: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const businessId = useSelector(selectActiveBusinessId);
    const customers = useSelector(selectCustomers);
    const isLoading = useSelector(selectIsLoadingCustomers);
    const error = useSelector(selectCustomersError);
    const nextCursor = useSelector(selectCustomersCursor);
    const isSyncing = useSelector(selectIsSyncing);

    const [query, setQuery] = useState<string>(searchParams.get("q") || "");

    const queryPayload = useMemo(() => {
        const p: any = { limit: 50 };
        if (query.trim()) p.q = query.trim();
        return p;
    }, [query]);

    useEffect(() => {
        if (!businessId) return;
        dispatch(fetchCustomers(queryPayload));
    }, [dispatch, businessId, queryPayload]);

    const applyQuery = (next: string) => {
        const params: Record<string, string> = {};
        if (next.trim()) params.q = next.trim();
        setSearchParams(params, { replace: true });
    };

    const onSync = async () => {
        if (!businessId) return;
        const result: any = await dispatch(syncCustomers(undefined));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Customers synced from QuickBooks.");
        } else {
            toast.error("Couldn't sync customers. Try again.");
        }
        dispatch(fetchCustomers(queryPayload));
    };

    const onRow = (c: ICollectCustomer) => navigate(`/collect/customers/${c._id}`);

    return (
        <CollectPageWrapper>
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    padding: { xs: "20px 12px", md: "32px 24px" },
                }}
            >
                <FlexBetween style={{ marginBottom: 8 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)",
                                color: colors.primary,
                            }}
                        >
                            Customers
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#6b7280",
                                fontSize: "0.9rem",
                                mt: 0.5,
                            }}
                        >
                            Everyone you bill, with their current outstanding
                            balance — synced from QuickBooks.
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <CollectGhostButton
                            startIcon={<Refresh fontSize="small" />}
                            onClick={() => dispatch(fetchCustomers(queryPayload))}
                            disabled={isLoading}
                        >
                            Refresh
                        </CollectGhostButton>
                        <CollectPrimaryButton
                            startIcon={
                                isSyncing ? (
                                    <CircularProgress
                                        size={16}
                                        sx={{ color: "#fff" }}
                                    />
                                ) : (
                                    <SyncAlt fontSize="small" />
                                )
                            }
                            onClick={onSync}
                            disabled={isSyncing || !businessId}
                        >
                            {isSyncing ? "Syncing…" : "Sync from QuickBooks"}
                        </CollectPrimaryButton>
                    </Box>
                </FlexBetween>

                {error && (
                    <CollectInfoBanner tone="warn">
                        <span>{error}</span>
                    </CollectInfoBanner>
                )}

                <CollectSectionCard>
                    <Box sx={{ mb: 2.5 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search by name, company, or email…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") applyQuery(query);
                            }}
                            onBlur={() => applyQuery(query)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { fontFamily: "'Montserrat', sans-serif" },
                            }}
                        />
                    </Box>

                    {isLoading && customers.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress
                                size={28}
                                sx={{ color: colors.mainSecondary }}
                            />
                        </Box>
                    ) : customers.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>No customers found</CollectEmptyTitle>
                            <CollectEmptyHint>
                                Run a sync to pull customers from QuickBooks, or
                                clear your search to see everyone.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>Customer</CollectTableHeadCell>
                                        <CollectTableHeadCell>Contact</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Open balance
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {customers.map((c) => (
                                        <TableRow
                                            key={c._id}
                                            hover
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => onRow(c)}
                                        >
                                            <CollectTableBodyCell>
                                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                    <span style={{ fontWeight: 700 }}>
                                                        {c.displayName}
                                                    </span>
                                                    {c.companyName &&
                                                        c.companyName !== c.displayName && (
                                                            <span
                                                                style={{
                                                                    fontSize: "0.78rem",
                                                                    color: "#6b7280",
                                                                }}
                                                            >
                                                                {c.companyName}
                                                            </span>
                                                        )}
                                                </Box>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 0.3,
                                                    }}
                                                >
                                                    {c.email && (
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 6,
                                                                fontSize: "0.82rem",
                                                            }}
                                                        >
                                                            <MailOutline
                                                                sx={{ fontSize: 14, color: "#6b7280" }}
                                                            />
                                                            {c.email}
                                                        </span>
                                                    )}
                                                    {c.phone && (
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 6,
                                                                fontSize: "0.82rem",
                                                                color: "#6b7280",
                                                            }}
                                                        >
                                                            <PhoneOutlined
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                            {c.phone}
                                                        </span>
                                                    )}
                                                    {!c.email && !c.phone && (
                                                        <span
                                                            style={{
                                                                fontSize: "0.82rem",
                                                                color: "#9ca3af",
                                                            }}
                                                        >
                                                            No contact on file
                                                        </span>
                                                    )}
                                                </Box>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <strong
                                                    style={{
                                                        color:
                                                            (c.balance || 0) > 0
                                                                ? colors.primary
                                                                : "#6b7280",
                                                    }}
                                                >
                                                    {formatMoney(c.balance || 0, c.currency)}
                                                </strong>
                                            </CollectTableBodyCell>
                                            <CollectTableBodyCell align="right">
                                                <Tooltip title="View customer">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRow(c);
                                                        }}
                                                    >
                                                        <OpenInNew fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </CollectTableBodyCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}

                    {nextCursor && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                            <CollectGhostButton
                                onClick={() =>
                                    dispatch(
                                        fetchCustomers({
                                            ...queryPayload,
                                            cursor: nextCursor,
                                        }),
                                    )
                                }
                                disabled={isLoading}
                            >
                                {isLoading ? "Loading…" : "Load next page"}
                            </CollectGhostButton>
                        </Box>
                    )}
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectCustomers;
